// Upsell orchestrator.
//
// Picks the active cross-sell provider and resolves the single upsell result to
// show for the current cart, following scenario precedence:
//   1. Pair / everything-set upgrade (bundle config)
//   2. Single-product bundle upgrade (bundle config)
//   3. General cross-sell from the active provider (Shopify / Rebuy)

import { resolveBundle } from "./bundles";
import { rebuyProvider } from "./rebuy-provider";
import { shopifyProvider } from "./shopify-provider";
import type {
  Cart,
  RecommendationProvider,
  UpsellConfig,
  UpsellResult,
} from "./types";

export function getProvider(name: UpsellConfig["provider"]): RecommendationProvider {
  return name === "rebuy" ? rebuyProvider : shopifyProvider;
}

const EMPTY: UpsellResult = { title: "", recommendations: [] };

export async function resolveUpsell(
  cart: Cart,
  config: UpsellConfig,
): Promise<UpsellResult> {
  if (!cart?.items?.length) return EMPTY;

  // Scenarios 1 & 2 — cart-aware bundle upgrades.
  const bundle = resolveBundle(cart, config.bundles, config.giftVariantId);
  if (bundle) return bundle;

  // Scenario 3 — general cross-sell from the active provider.
  const provider = getProvider(config.provider);
  const recommendations = await provider.getComplementary(
    cart,
    config.limit,
    config,
  );

  if (recommendations.length === 0) return EMPTY;

  return { title: config.complementaryTitle, recommendations };
}

export type {
  Cart,
  UpsellConfig,
  UpsellRecommendation,
  UpsellResult,
} from "./types";
